import React from 'react';
import {
  LayoutGrid, Sprout, CalendarDays, Bell, Download, ShieldCheck,
  ChevronRight, LogOut, Check,
} from 'lucide-react';
import { usePrefs, clearPrefs } from '../../lib/prefs';
import { useFlag } from '../../lib/flags';
import { recordConsent } from '../../lib/consent';
import { signOut, signOutAllDevices, useSession } from '../../lib/session';
import { clearLocalTrackerData, getStore } from '../../features/tracker/store';
import { sgToday } from '../../lib/dates';
import { centerToast } from '../../components/CenterToast';
import InstallScreen from '../../components/InstallScreen';

/**
 * Settings — the third segment of Profile.
 *
 * Deliberately not a new top-level screen: everything personal already lives
 * behind the avatar, and a fourth place to look would only fragment it.
 */

const HOME_CHOICES = [
  {
    id: 'services',
    label: 'Services',
    hint: 'Browse what MTFA offers',
    icon: LayoutGrid,
    flag: null,
  },
  {
    id: 'tracker',
    label: 'Tracker',
    hint: 'Open on your daily log',
    icon: Sprout,
    flag: 'tracker',
  },
  {
    id: 'updates',
    label: 'Updates',
    hint: "See what's coming up",
    icon: CalendarDays,
    flag: 'updates_view',
  },
];

function SectionLabel({ children }) {
  return (
    <h2 className="px-4 pt-5 pb-2 text-[12px] uppercase tracking-wider font-semibold text-muted-foreground">
      {children}
    </h2>
  );
}

function Row({ icon: Icon, title, subtitle, onClick, disabled, testId, right }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="w-full px-4 py-3.5 flex items-center gap-3 text-left border-b border-border/60 disabled:opacity-40 transition-colors hover:bg-muted/40 disabled:hover:bg-transparent"
    >
      {Icon && <Icon className="h-5 w-5 flex-shrink-0 text-[#0F4C4A]" />}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        {subtitle && (
          <span className="block text-xs text-muted-foreground leading-snug mt-0.5">{subtitle}</span>
        )}
      </span>
      {right || <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
    </button>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${
        checked ? 'bg-[#0F4C4A]' : 'bg-muted-foreground/30'
      }`}
    >
      <span
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export default function Settings({ onNavigate, onOpenTracker }) {
  const { prefs, setPref } = usePrefs();
  const session = useSession();
  const trackerOn = useFlag('tracker');
  const updatesOn = useFlag('updates_view');
  const communityOn = useFlag('tracker_community');
  const [installOpen, setInstallOpen] = React.useState(false);

  // Mirrors what was ticked at sign-up. The ledger is the evidence; this is the
  // control. Withdrawing has to be exactly as easy as opting in — one tap, no
  // confirmation maze, no "are you sure you'll miss out".
  const [marketingOn, setMarketingOn] = React.useState(
    () => Boolean(session?.meta?.consent?.marketing),
  );

  const enabled = { tracker: trackerOn, updates_view: updatesOn };
  const homeChoices = HOME_CHOICES.filter((choice) => !choice.flag || enabled[choice.flag]);

  const toggleMarketing = (next) => {
    setMarketingOn(next);
    recordConsent('marketing_email', next);
    centerToast(next ? 'You’ll hear from us' : 'Updates turned off', next ? '💌' : '🔕');
  };

  const toggleCommunity = (next) => {
    setPref('community_opt_in', next);
    recordConsent('community_pulse', next);
  };

  return (
    <div className="bg-background pb-8" data-testid="settings-page">
      {homeChoices.length > 1 && (
        <>
          <SectionLabel>Home screen</SectionLabel>
          <div className="px-4 grid grid-cols-3 gap-2">
            {homeChoices.map((choice) => {
              const Icon = choice.icon;
              const active = prefs.home_view === choice.id;
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => setPref('home_view', choice.id)}
                  data-testid={`settings-home-${choice.id}`}
                  aria-pressed={active}
                  className={`relative rounded-2xl border p-3 text-center flex flex-col items-center gap-1.5 transition-colors ${
                    active ? 'border-[#0F4C4A] bg-[#E8F5E9]' : 'border-border bg-white'
                  }`}
                >
                  {active && (
                    <span className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-[#0F4C4A] flex items-center justify-center">
                      <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  <Icon className="h-5 w-5 text-[#0F4C4A]" />
                  <span className="text-xs font-bold text-foreground leading-tight">{choice.label}</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">{choice.hint}</span>
                </button>
              );
            })}
          </div>
          <p className="px-4 pt-2 text-[11px] text-muted-foreground leading-relaxed">
            Which view IhsanOne opens on. You can still switch at the top of Home any time.
          </p>
        </>
      )}

      <SectionLabel>My trackers</SectionLabel>
      <Row
        icon={Sprout}
        title="Manage my trackers"
        subtitle={trackerOn ? 'Reorder or change your line on Home › Tracker' : 'Available soon'}
        disabled={!trackerOn}
        testId="settings-trackers"
        onClick={() => onOpenTracker?.()}
      />

      <SectionLabel>Notifications & consent</SectionLabel>
      <Row
        icon={Bell}
        title="Be first to know"
        subtitle="Bursaries, events and volunteer calls — by WhatsApp or email."
        testId="settings-marketing"
        right={<Toggle checked={marketingOn} onChange={toggleMarketing} label="Receive updates from MTFA" />}
        onClick={() => toggleMarketing(!marketingOn)}
      />
      {communityOn && (
        <Row
          icon={Bell}
          title="Add my totals to the community pulse"
          subtitle="Anonymous. No names, no rankings — just all of us, together."
          testId="settings-community"
          right={(
            <Toggle
              checked={Boolean(prefs.community_opt_in)}
              onChange={toggleCommunity}
              label="Contribute to the community pulse"
            />
          )}
          onClick={() => toggleCommunity(!prefs.community_opt_in)}
        />
      )}

      <SectionLabel>App</SectionLabel>
      <Row
        icon={Download}
        title="Install IhsanOne"
        subtitle="Add it to your home screen"
        testId="settings-install"
        onClick={() => setInstallOpen(true)}
      />

      <SectionLabel>Privacy</SectionLabel>
      <Row
        icon={ShieldCheck}
        title="Privacy notice"
        subtitle="How we use and protect your data"
        testId="settings-privacy"
        onClick={() => window.open('/privacy.html', '_blank', 'noopener')}
      />
      <Row
        title="Export my tracker data"
        subtitle={trackerOn ? 'Download everything you have logged' : 'Available once the tracker is switched on'}
        disabled={!trackerOn}
        testId="settings-export"
        onClick={async () => {
          const data = await getStore().exportAll();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `ihsanone-tracker-${sgToday()}.json`;
          link.click();
          URL.revokeObjectURL(url);
        }}
      />
      <Row
        title="Delete my tracker data"
        subtitle={trackerOn ? 'Permanently removes every entry' : 'Available once the tracker is switched on'}
        disabled={!trackerOn}
        testId="settings-delete"
        onClick={async () => {
          // Typed confirmation, because this cannot be undone. Deliberately not
          // a one-tap action, unlike withdrawing consent.
          // eslint-disable-next-line no-alert
          const typed = window.prompt('This permanently deletes every tracker entry. Type DELETE to confirm.');
          if (typed !== 'DELETE') return;
          await getStore().deleteAll();
          clearLocalTrackerData();
          centerToast('Your tracker data is deleted', '🗑️');
        }}
      />

      <div className="px-4 pt-6 space-y-3">
        <button
          type="button"
          data-testid="settings-signout"
          onClick={() => {
            // Preferences are cached per device; clearing them stops one
            // member's settings greeting the next person on a shared phone.
            // The tracker outbox goes too — an unsent worship log must never
            // sit on a device waiting to be sent under someone else's session.
            clearPrefs();
            clearLocalTrackerData();
            signOut();
            onNavigate?.('home');
          }}
          className="w-full h-12 rounded-full border border-border bg-white text-sm font-bold text-rose-600 flex items-center justify-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
        <button
          type="button"
          data-testid="settings-signout-all"
          onClick={async () => {
            // Server-enforced global revocation: revokes every access token
            // for the account (not just this device), kills every refresh
            // token, and blanks the local session. Use when a phone is lost
            // or a password is compromised.
            // eslint-disable-next-line no-alert
            const ok = window.confirm(
              'Sign out of every device that\'s using your IhsanOne account? '
              + 'You\'ll need to sign in again on each one. This cannot be undone.',
            );
            if (!ok) return;
            const res = await signOutAllDevices();
            clearPrefs();
            clearLocalTrackerData();
            if (res.ok) {
              centerToast('Signed out everywhere', '🔒');
            } else {
              centerToast(res.error || 'Could not sign out — try again.', '⚠️');
            }
            onNavigate?.('home');
          }}
          className="w-full h-11 rounded-full border border-rose-200 bg-rose-50 text-sm font-semibold text-rose-700 flex items-center justify-center gap-2"
        >
          <ShieldCheck className="h-4 w-4" />
          Sign out of all devices
        </button>
      </div>

      {installOpen && (
        <InstallScreen
          onDismiss={() => setInstallOpen(false)}
          onInstalled={() => setInstallOpen(false)}
        />
      )}
    </div>
  );
}
